/**
 * Table Renderer Helper Functions
 *
 * Utility functions for formatting and data manipulation
 */

import type { Lot, PortalSettings } from '../../types';
import { escapeHtml, escapeAttr, formatMeatOnBoneValue } from '../formatting';
import { formatLocationWithDistance as formatLocationWithDistanceFromDistance } from '../distance';
import { formatPriceStatusValue } from '../utils';
import { estimateSkeleton } from '../skeletons';
import { getSourceDisplay } from '../formatting';

declare global {
  interface Window {
    __distanceCache?: Record<string, number | 'pending' | null>;
  }
}

/**
 * Generate skeleton placeholder for estimate field.
 */
export { estimateSkeleton } from '../skeletons';

/**
 * Format meat-on-bone value for display.
 */
export function formatMeatValue(value: number | null | undefined): string {
  if (value == null) return '—';
  return formatMeatOnBoneValue(value);
}

/**
 * Format location with distance.
 */
export function formatLocationWithDistance(
  location?: string,
  homeZip?: string,
  settings?: PortalSettings | null
): string {
  if (!location) return '-';
  return formatLocationWithDistanceFromDistance(location, homeZip || '', settings || null);
}

/**
 * Get source display label or logo.
 * Re-exported from formatting module which now handles extension source normalization.
 */
export { getSourceDisplay } from '../formatting';

/**
 * Build price status content HTML.
 */
export function buildPriceStatusContent(lot?: Lot | null): string {
  if (!lot) return '';
  const status = lot.aiStatus;
  if (status === 'processing' || status === 'pending') {
    return '<span class="skeleton skeleton--inline skeleton--price-status"></span>';
  }
  const priceStatus = formatPriceStatusValue(lot);
  return priceStatus ? escapeHtml(priceStatus) : '';
}

/**
 * Type definitions for helper functions
 */
export type GetEffectiveCloseTimeFn = (lot: Lot) => number | string | null | undefined;
export type HasCustomCloseTimeFn = (lotId?: string | null) => boolean;
